/* Drives the phone product gallery: snippets/tuti-mobile-gallery.liquid.

   The gallery holds every photo of every shade in one continuous run,
   so there is nothing to fetch and nothing to re-render — all this code
   does is move two scrollers in step and keep the shade picker pointing
   at whatever is on screen.

   Three inputs, one derived state. The track's own scrollLeft is the
   single source of truth for which photo is current; the arrows, the
   rail and the picker are all downstream of it. Nothing here holds an
   index of its own that could drift out of step with the scroller.

   The picker binding runs both ways, which is the one place a feedback
   loop could form: landing on a photo checks that shade's radio, and
   checking a radio scrolls to that shade. `data-selected-variant` on
   the root is what breaks it — it is written BEFORE the radio is
   dispatched, so the echo that comes back finds the value already
   current and stops there. */
(function () {
  var root = document.querySelector('[data-tuti-mgal]');
  if (!root) return;

  var track = root.querySelector('[data-mgal-track]');
  var rail = root.querySelector('[data-mgal-rail]');
  var railTrack = root.querySelector('[data-mgal-rail-track]');
  if (!track) return;

  var slides = Array.prototype.slice.call(track.querySelectorAll('.tuti-mgal__slide'));
  var thumbs = railTrack ? Array.prototype.slice.call(railTrack.querySelectorAll('.tuti-mgal__thumb')) : [];
  var prevButton = root.querySelector('[data-mgal-prev]');
  var nextButton = root.querySelector('[data-mgal-next]');
  var picker = document.querySelector('variant-picker');

  if (slides.length < 2) return;

  var frame = null;
  var variantTimer = null;
  var lastIndex = -1;

  /* Above 750px the whole gallery is display:none and the bento wall
     has the floor. Everything below is cheap enough to leave bound, but
     anything with a side effect outside this component — checking a
     radio, which re-fetches the picker — has to know it is off duty. */
  function isActive() {
    return root.offsetParent !== null;
  }

  /* Exact, not approximate: the CSS gives every slide `flex: 0 0 100%`
     in a gapless, unpadded track, so slide N begins at exactly N track
     widths. Worth relying on — the alternative is measuring thirty-odd
     client rects on every scroll frame. If the stage ever gains a
     gutter or a peek, this is the line that has to change. */
  function currentIndex() {
    var width = track.clientWidth || 1;
    var index = Math.round(track.scrollLeft / width);
    return Math.max(0, Math.min(slides.length - 1, index));
  }

  /* Safari only learned ScrollToOptions in 15.4. Before that
     `scrollTo({left: n})` is read as the two-argument form with both
     arguments undefined — which scrolls to zero, i.e. silently jumps to
     the wrong photo rather than merely skipping the animation. Falling
     back to a plain assignment loses the glide and keeps the gallery
     correct, which is the right way round. */
  var canAnimateScroll = 'scrollBehavior' in document.documentElement.style;

  function glide(scroller, left) {
    if (canAnimateScroll) {
      scroller.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      scroller.scrollLeft = left;
    }
  }

  function scrollToIndex(index) {
    var target = Math.max(0, Math.min(slides.length - 1, index));
    glide(track, target * track.clientWidth);
  }

  /* ---- Rail ---- */

  function markRailEnd() {
    if (!rail || !railTrack) return;
    /* 1px of slack: fractional scroll positions on high-DPR screens
       mean scrollLeft rarely lands exactly on the maximum, and without
       the tolerance the fade never quite switches off. */
    var atEnd = railTrack.scrollLeft + railTrack.clientWidth >= railTrack.scrollWidth - 1;
    rail.setAttribute('data-at-end', atEnd ? 'true' : 'false');
  }

  function centreThumb(index) {
    if (!railTrack || !thumbs[index]) return;
    var thumb = thumbs[index];
    /* Centred, not merely nudged into view. With one thumbnail per
       photo the rail is far longer than the screen, so the useful
       question is "what is on either side of where I am", and only
       centring answers it. Clamped at both ends so the row never
       scrolls past its own content to satisfy the centre. */
    var target = thumb.offsetLeft + thumb.offsetWidth / 2 - railTrack.clientWidth / 2;
    var limit = railTrack.scrollWidth - railTrack.clientWidth;
    glide(railTrack, Math.max(0, Math.min(limit, target)));
  }

  /* ---- Shade picker ---- */

  function selectVariant(variantId) {
    if (!picker || !variantId) return;
    // Already current: either nothing changed, or this is the echo of a
    // change we ourselves just made. Either way there is nothing to do.
    if (root.dataset.selectedVariant === variantId) return;

    var input = picker.querySelector('input[type="radio"][data-variant-id="' + variantId + '"]');
    if (!input) return;

    // Written before the dispatch, so the ProductSelectEvent this
    // triggers comes back to a root that already agrees and stops.
    root.dataset.selectedVariant = variantId;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---- The one state change everything hangs off ---- */

  function syncTo(index) {
    if (index === lastIndex) return;
    lastIndex = index;

    for (var i = 0; i < thumbs.length; i++) {
      if (i === index) {
        thumbs[i].setAttribute('aria-current', 'true');
      } else {
        thumbs[i].removeAttribute('aria-current');
      }
    }
    centreThumb(index);

    if (prevButton) prevButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= slides.length - 1;

    /* Debounced, unlike everything above it. Swiping across a shade
       boundary is instant feedback; swiping across nine of them should
       not fire nine section requests. The picker aborts its own
       in-flight fetch, so this is belt and braces — but it also stops
       the URL's ?variant from being rewritten on every frame of a
       momentum scroll. */
    /* The shade CIRCLE follows the gallery from right here: this is the
       one place that knows which slide is current, so it is the one
       place that should say so. (Landing on a slide used to also check
       that shade's radio — that stays off: paging the photos must not
       change the shopper's selection. selectVariant is kept below for
       the picker-driven path's bookkeeping.) */
    /* Debounced on the same timer the old selectVariant path used, and
       for the same reason: this runs on every frame of a scroll, and at
       a shade boundary the index flip-flops between the two sides, which
       made the circle jitter. Only the slide still current 180ms after
       the last movement gets painted. */
    if (variantTimer) clearTimeout(variantTimer);
    variantTimer = setTimeout(function () {
      var bubbleSlide = slides[index];
      if (bubbleSlide && window.tutiShadeBubble) {
        window.tutiShadeBubble(bubbleSlide.dataset.variantId);
      }
    }, 180);
    return;
  }

  function onTrackScroll() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      syncTo(currentIndex());
    });
  }

  /* ---- Wiring ---- */

  track.addEventListener('scroll', onTrackScroll, { passive: true });

  if (railTrack) {
    railTrack.addEventListener('scroll', markRailEnd, { passive: true });
  }

  thumbs.forEach(function (thumb, index) {
    thumb.addEventListener('click', function () {
      scrollToIndex(index);
      // Don't wait for the scroll to report back: the rail should move
      // under the finger that tapped it, not a frame later.
      syncTo(index);
    });
  });

  if (prevButton) {
    prevButton.addEventListener('click', function () {
      scrollToIndex(currentIndex() - 1);
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', function () {
      scrollToIndex(currentIndex() + 1);
    });
  }

  /* Picker -> gallery. Tapping a swatch jumps to that shade's first
     photo. The guard is the same one selectVariant uses from the other
     direction: if the root already names this variant, the event is our
     own echo and there is nothing to move. */
  if (picker) {
    picker.addEventListener('shopify:product:select', function (event) {
      var variantId = event.detail && event.detail.variantId;
      if (!variantId || root.dataset.selectedVariant === String(variantId)) return;

      root.dataset.selectedVariant = String(variantId);

      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.variantId === String(variantId)) {
          scrollToIndex(i);
          syncTo(i);
          return;
        }
      }
    });
  }

  /* ---- The promo pill ("20% off first order") ----

     On phones it sits on the photograph, bottom left; everywhere else
     it stays down by the price in the details column. (The eyebrow used
     to take this spot; it now stays in the column, under the rating.)

     The node is moved, because neither of the alternatives works. It is
     a theme block, so it cannot be rendered into the gallery from
     Liquid; and it cannot be positioned over the photo with CSS either,
     because every Horizon group-block wrapper between it and the shared
     ancestor is position:relative — an absolutely positioned pill
     anchors to its own wrapper, which sits part way down the details
     column, not to the photo.

     Moved rather than duplicated: a copy would read the same sentence
     twice to a screen reader and would need keeping in sync with a
     merchant-editable setting. A comment node holds its place so the
     move is exactly reversible.

     Both listeners are deliberate. The stranded state — pill left in
     the gallery after the viewport grows past 750px — hides it
     completely, because the gallery is display:none up there, and no
     CSS can rescue a child of a display:none parent. matchMedia is the
     precise signal; resize is the backstop for any environment that
     does not deliver the change event. */
  var eyebrow = document.querySelector('.product-details .tuti-promo-line');
  var bar = root.querySelector('[data-mgal-bar]');

  if (eyebrow && bar) {
    var anchor = document.createComment('tuti-promo-home');
    eyebrow.parentNode.insertBefore(anchor, eyebrow);

    var phone = window.matchMedia('(max-width: 749px)');

    var placeEyebrow = function () {
      if (phone.matches) {
        if (eyebrow.parentNode !== bar) bar.appendChild(eyebrow);
        eyebrow.classList.add('tuti-mgal__eyebrow');
      } else if (eyebrow.parentNode === bar) {
        anchor.parentNode.insertBefore(eyebrow, anchor.nextSibling);
        eyebrow.classList.remove('tuti-mgal__eyebrow');
      }
    };

    placeEyebrow();

    if (phone.addEventListener) {
      phone.addEventListener('change', placeEyebrow);
    } else if (phone.addListener) {
      phone.addListener(placeEyebrow);
    }
    window.addEventListener('resize', placeEyebrow, { passive: true });
  }

  /* Initial state. The inline script in the snippet has already put the
     track on the right slide, so this reads it rather than setting it. */
  syncTo(currentIndex());
  markRailEnd();
  /* The rail's scrollWidth depends on lazy thumbnails that may not have
     laid out yet on first paint, so the initial end-check can read a
     track that is not scrollable and hide the fade wrongly. */
  window.addEventListener('load', markRailEnd, { once: true });
})();
